/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ICellRendererParams } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { isRosterSpareConfirmed } from '@sportninja/common/features/game/attendance/utils/rosterSpareConfirm'
import { paragraphXSmall } from 'src/components/css'
import {
  ROSTER_STATUS_PILLS,
  SOCCER_STATUS_PILLS,
  SPARE_STATUS_PILLS,
  SPARE_CONFIRMED_PILL,
  SPARE_NOT_CONFIRMED_PILL,
} from '../../constants/rosterStatusPills'
import type { GameRosterPlayer, RosterStatusContext } from '../../types'
import { getPlayerRosterStatus } from '../../utils/getPlayerRosterStatus'
import RosterPill from '../RosterPill'

export interface PlayerStatusCellParams extends RosterStatusContext {
  // Spares grid: also show the team official's Confirmed / Not Confirmed.
  isSpares: boolean
  // Soccer game: the starting goalie pill reads Starting Keeper.
  isSoccer: boolean
}

/** STATUS column: the player's status pill, plus a spare's confirmation pill. */
const PlayerStatusCell = ({
  data,
  isSpares,
  isSoccer,
  ...context
}: ICellRendererParams<GameRosterPlayer> & PlayerStatusCellParams) => {
  if (!data) {
    return null
  }

  const status = getPlayerRosterStatus(data, context)
  // Confirmation is attendance info — same viewers as the availability pill.
  const showSpareConfirm = isSpares && context.canViewAttendanceInfo

  return (
    <div css={wrapStyle}>
      {status && status.kind !== 'no_response' ? (
        <RosterPill
          config={
            (isSpares && SPARE_STATUS_PILLS[status.kind]) ||
            (isSoccer && SOCCER_STATUS_PILLS[status.kind]) ||
            ROSTER_STATUS_PILLS[status.kind]
          }
        />
      ) : null}
      {status?.kind === 'suspended' ? (
        <span css={detailStyle}>
          {`${status.startDate} - ${status.endDate}`}
        </span>
      ) : null}
      {showSpareConfirm ? (
        <RosterPill
          config={
            isRosterSpareConfirmed(data)
              ? SPARE_CONFIRMED_PILL
              : SPARE_NOT_CONFIRMED_PILL
          }
        />
      ) : status?.kind === 'no_response' ? (
        // No answer yet and nothing else to show — the placeholder dash.
        <span>-</span>
      ) : null}
    </div>
  )
}

export default PlayerStatusCell

// ---- styled ----
const wrapStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`
const detailStyle = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
`
